// Add window type declaration for Safari compatibility
declare global {
  interface Window {
    webkitAudioContext: typeof AudioContext;
    webkitOfflineAudioContext: typeof OfflineAudioContext;
  }
}

// Use a cross-browser compatible AudioContext
const AudioContextClass = window.AudioContext || window.webkitAudioContext;
const OfflineAudioContextClass = window.OfflineAudioContext || window.webkitOfflineAudioContext;

import { useState, useRef, useEffect } from "react";
import { useToast } from "@/hooks/use-toast";

const MAX_RECORDING_TIME = 60; // 1 minute in seconds

async function convertAudioToMono(file: File | Blob): Promise<Blob> {
  console.log('Starting audio conversion...');
  const audioContext = new AudioContextClass({ sampleRate: 16000 });
  const arrayBuffer = await file.arrayBuffer();
  const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);

  // Create offline context for processing
  const offlineContext = new OfflineAudioContextClass(1, audioBuffer.length, 16000);
  const source = offlineContext.createBufferSource();
  source.buffer = audioBuffer;
  source.connect(offlineContext.destination);
  source.start();

  // Render audio
  const renderedBuffer = await offlineContext.startRendering();

  // Convert to WAV format with optimized settings
  const length = renderedBuffer.length * 2;
  const buffer = new ArrayBuffer(44 + length);
  const view = new DataView(buffer);

  // WAV header with optimized settings
  const writeString = (view: DataView, offset: number, string: string) => {
    for (let i = 0; i < string.length; i++) {
      view.setUint8(offset + i, string.charCodeAt(i));
    }
  };

  writeString(view, 0, "RIFF");
  view.setUint32(4, 36 + length, true);
  writeString(view, 8, "WAVE");
  writeString(view, 12, "fmt ");
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, 1, true);
  view.setUint32(24, 16000, true);
  view.setUint32(28, 32000, true);
  view.setUint16(32, 2, true);
  view.setUint16(34, 16, true);
  writeString(view, 36, "data");
  view.setUint32(40, length, true);

  // Write audio data with optimized buffer handling
  const data = new Float32Array(renderedBuffer.getChannelData(0));
  const chunk_size = 1024; // Process in smaller chunks for better performance
  let offset = 44;
  
  for (let i = 0; i < data.length; i += chunk_size) {
    const chunk_end = Math.min(i + chunk_size, data.length);
    for (let j = i; j < chunk_end; j++) {
      const sample = Math.max(-1, Math.min(1, data[j]));
      view.setInt16(offset, sample < 0 ? sample * 0x8000 : sample * 0x7fff, true);
      offset += 2;
    }
  }

  console.log('Audio conversion completed');
  return new Blob([buffer], { type: "audio/wav" });
}

export const useVoiceRecording = () => {
  const { toast } = useToast();
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [isConverting, setIsConverting] = useState(false);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const recordingTimerRef = useRef<number | null>(null);

  useEffect(() => {
    if (isRecording) {
      recordingTimerRef.current = window.setInterval(() => {
        setRecordingTime(prev => {
          if (prev >= MAX_RECORDING_TIME) {
            stopRecording();
            return 0;
          }
          return prev + 1;
        });
      }, 1000);
    } else {
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
      }
      setRecordingTime(0);
    }

    return () => {
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
      }
    };
  }, [isRecording]);

  const startRecording = async () => {
    try {
      console.log('Starting recording...');
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          sampleRate: 16000
        } 
      });
      
      // Optimize audio settings
      const audioTrack = stream.getAudioTracks()[0];
      await audioTrack.applyConstraints({
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
      });
      
      console.log('Audio stream obtained with settings:', audioTrack.getSettings());
      
      // Use optimized MIME type selection
      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus') 
        ? 'audio/webm;codecs=opus'
        : MediaRecorder.isTypeSupported('audio/mp4') 
          ? 'audio/mp4'
          : 'audio/ogg;codecs=opus';

      console.log('Selected MIME type:', mimeType);

      const mediaRecorder = new MediaRecorder(stream, {
        mimeType,
        bitsPerSecond: 128000 // Optimize bitrate for voice
      });
      
      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];

      // Optimize data handling with smaller chunks
      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunksRef.current.push(e.data);
        }
      };

      mediaRecorder.start(250); // Reduce chunk size for faster processing
      console.log('MediaRecorder started with state:', mediaRecorder.state);
      setIsRecording(true);

      // Auto-stop after max duration
      setTimeout(() => {
        if (mediaRecorderRef.current?.state === 'recording') {
          stopRecording();
          toast({
            title: "Recording limit reached",
            description: "Maximum recording time is 1 minute",
            variant: "default",
          });
        }
      }, MAX_RECORDING_TIME * 1000);

    } catch (error) {
      console.error('Recording error:', error);
      toast({
        title: "Error",
        description: "Failed to access microphone. Please check your permissions.",
        variant: "destructive",
      });
    }
  };

  const stopRecording = () => {
    console.log('Stopping recording...');
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      console.log('Final recorder state:', mediaRecorderRef.current.state);
      setIsRecording(false);
      if (mediaRecorderRef.current.stream) {
        mediaRecorderRef.current.stream.getTracks().forEach(track => track.stop());
      }
    }
  };

  const getRecordingData = async (): Promise<{ chunks: Blob[], mimeType: string } | null> => {
    if (mediaRecorderRef.current) {
      setIsConverting(true);
      try {
        const originalBlob = new Blob(chunksRef.current, { 
          type: mediaRecorderRef.current.mimeType 
        });
        console.log('Original recording:', {
          size: originalBlob.size,
          type: originalBlob.type
        });

        console.log('Converting audio to optimized format...');
        const convertedBlob = await convertAudioToMono(originalBlob);
        console.log('Converted recording:', {
          size: convertedBlob.size,
          type: convertedBlob.type
        });
        
        return {
          chunks: [convertedBlob],
          mimeType: convertedBlob.type
        };
      } catch (error) {
        console.error('Error processing recording:', error);
        toast({
          title: "Error",
          description: "Failed to process audio recording. Please try again.",
          variant: "destructive",
        });
        return null;
      } finally {
        setIsConverting(false);
      }
    }
    return null;
  };

  return {
    isRecording,
    recordingTime,
    isConverting,
    startRecording,
    stopRecording,
    getRecordingData
  };
};
